import { MutableRefObject, useState } from 'react';
import { Mesh, Scene, Vector3 } from 'three';
import {
	buildPhotonMapWithGridParallel,
	clearAllPhotons,
	getPhotonEnergyHeatmapLegendStats,
	refreshPhotonEnergyHeatmap,
	getPhotonAccumulationStats,
	PHOTON_MAP_CONFIG,
	PhotonHeatmapNormalizationMode,
	hidePhotonVisualizations,
	showPhotonVisualizations,
	setPhotonEnergyHeatmapCellSize,
	setPhotonEnergyHeatmapEnabled,
	setPhotonEnergyHeatmapNormalizationMode,
	setPhotonVisualizationBounceRange,
	updateSunPositionVisualization,
} from '@/utils/rayTracing';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import styles from './styles.module.scss';
import { FormaTab, FormaTabs } from '../UI/FormaTabs';
import { WeaveCheckbox } from '../UI/WeaveCheckbox/WeaveCheckbox';
import { clampNumber } from '@/utils/general';

export interface PhotonMappingControlsProps {
	sceneRef: MutableRefObject<Scene | null>;
	currentSunPositionRef: MutableRefObject<Vector3>;
	currentDate: Date;
	currentTime: string;
	setCurrentTime: (time: string) => void;
	latitude: number | null;
	longitude: number | null;
	onBeforeRunPhotonMapping?: () => Promise<void>;
	onAfterClearPhotons?: () => Promise<void>;
}

export const PhotonMappingControls = ({
	sceneRef,
	currentSunPositionRef,
	currentDate,
	currentTime,
	setCurrentTime,
	latitude,
	longitude,
	onBeforeRunPhotonMapping,
	onAfterClearPhotons,
}: PhotonMappingControlsProps) => {
	const {
		photonStatsRow,
		photonControls,
		photonControlsButtons,
		primaryButtons,
	} = styles;

	const [isCalculating, setIsCalculating] = useState(false);
	const [isClearing, setIsClearing] = useState(false);
	const [isEnablingPhotonMapping, setIsEnablingPhotonMapping] = useState(false);
	const [isSweeping, setIsSweeping] = useState(false);
	const [showEnergyHeatmap, setShowEnergyHeatmap] = useState(false);
	const [heatmapNormalizationMode, setHeatmapNormalizationMode] =
		useState<PhotonHeatmapNormalizationMode | null>(null);
	const [heatmapLegend, setHeatmapLegend] = useState(getPhotonEnergyHeatmapLegendStats());
	const [heatmapCellSize, setHeatmapCellSize] = useState(
		getPhotonEnergyHeatmapLegendStats().cellSize || 1
	);
	const [calculationProgress, setCalculationProgress] = useState(0);
	const [photonStats, setPhotonStats] = useState(getPhotonAccumulationStats());
	const [buildingsHidden, setBuildingsHidden] = useState(false);
	const [photonsVisible, setPhotonsVisible] = useState(true);
	const hasExtrudedWalls = useThreeSceneStore((state) => state.hasExtrudedWalls);
	const isBusy = isCalculating || isClearing || isSweeping || isEnablingPhotonMapping;
	const [sweepStartHour, setSweepStartHour] = useState(0);
	const [sweepEndHour, setSweepEndHour] = useState(23);
	const [sweepStepHours, setSweepStepHours] = useState(1);
	const [atmosphericClarity, setAtmosphericClarity] = useState(100);
	const [cloudCover, setCloudCover] = useState(0);
	const [minBounce, setMinBounce] = useState(
		PHOTON_MAP_CONFIG.visualizationConfig.minBouncesToShow
	);
	const [maxBounce, setMaxBounce] = useState(
		PHOTON_MAP_CONFIG.visualizationConfig.maxBouncesToShow
	);

	const runPhotonMapping = async (options?: { skipScenePreparation?: boolean }) => {
		const { skipScenePreparation = false } = options ?? {};
		if (!sceneRef.current || !currentSunPositionRef.current) return;
		const liveStats = getPhotonAccumulationStats();
		if (!liveStats.canAddMore) {
			alert('Maximum photon limit reached! Clear photons to continue.');
			return;
		}
		setPhotonStats(liveStats);
		// Update sun position visualization
		updateSunPositionVisualization(sceneRef.current, currentSunPositionRef.current);

		if (!skipScenePreparation && onBeforeRunPhotonMapping) {
			await onBeforeRunPhotonMapping();
		}
		setCalculationProgress(0);
		setIsCalculating(true);
		try {
			await buildPhotonMapWithGridParallel(
				currentSunPositionRef.current,
				sceneRef.current,
				(progress, cellInfo) => {
					console.log(progress, cellInfo);
					setCalculationProgress(Math.round(progress * 100));
					if (cellInfo) {
						console.log(`📊 Processing cell ${cellInfo.current}/${cellInfo.total}`);
					}
				},
				{
					physicalContext: {
						date: currentDate,
						timeString: currentTime,
						latitude,
						longitude,
						atmosphericClarity: atmosphericClarity / 100,
						cloudCover: cloudCover / 100,
					},
				}
			);

			setPhotonStats(getPhotonAccumulationStats());
			showPhotonVisualizations();
			setPhotonsVisible(true);
			if (showEnergyHeatmap && sceneRef.current) {
				refreshPhotonEnergyHeatmap(sceneRef.current);
				setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
			}
		} finally {
			setIsCalculating(false);
		}
	};

	const handleRunPhotonMapping = async () => {
		await runPhotonMapping();
	};

	const resetPhotonUiAfterClear = () => {
		setPhotonStats(getPhotonAccumulationStats());
		setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
		setCalculationProgress(0);
		setBuildingsHidden(false);
		setPhotonsVisible(false);
	};

	const clearPhotonStateWithoutRebuild = async () => {
		if (!sceneRef.current || !currentSunPositionRef.current) return;

		setIsClearing(true);
		try {
			clearAllPhotons(sceneRef.current);
			resetPhotonUiAfterClear();
		} catch (error) {
			console.error('Failed to apply atmospheric settings:', error);
		} finally {
			setIsClearing(false);
		}
	};

	const handleApplyAndRunPhotonMapping = async () => {
		await clearPhotonStateWithoutRebuild();

		await runPhotonMapping({ skipScenePreparation: true });
	};

	const handleClearPhotons = async () => {
		if (!sceneRef.current) return;
		setIsClearing(true);
		try {
			clearAllPhotons(sceneRef.current);
			if (onAfterClearPhotons) {
				await onAfterClearPhotons();
			}
			resetPhotonUiAfterClear();
		} catch (error) {
			console.error('Failed to rebuild scene after clearing photons:', error);
		} finally {
			setIsClearing(false);
		}
	};

	const handleEnablePhotonMapping = async () => {
		if (!onBeforeRunPhotonMapping) return;
		setIsEnablingPhotonMapping(true);
		try {
			await onBeforeRunPhotonMapping();
		} catch (error) {
			console.error('Failed to enable photon mapping scene:', error);
		} finally {
			setIsEnablingPhotonMapping(false);
		}
	};

	const handleTogglePhotonVisibility = () => {
		if (!sceneRef.current) return;

		if (photonsVisible) {
			hidePhotonVisualizations(sceneRef.current);
			setPhotonsVisible(false);
			return;
		}

		showPhotonVisualizations();
		setPhotonsVisible(true);
	};

	const handleToggleBuildingVisibility = () => {
		if (!sceneRef.current) return;
		const nextHidden = !buildingsHidden;
		const hiddenTargetUuids = new Set<string>();

		sceneRef.current.traverse((child) => {
			if (child.userData?.isWindow || child.userData?.category === 'window') {
				child.visible = !nextHidden;
				hiddenTargetUuids.add(child.uuid);
				return;
			}

			if (child instanceof Mesh && child.userData.category === 'building') {
				child.visible = !nextHidden;
				hiddenTargetUuids.add(child.uuid);
			}
		});

		sceneRef.current.traverse((child) => {
			if (!child.userData) return;
			// Handle merged building edges (single object for all edges)
			if (child.userData.isAllBuildingEdges) {
				child.visible = !nextHidden;
				return;
			}
			const parentUuid = child.userData.parentMeshUuid;
			if (!parentUuid || !hiddenTargetUuids.has(parentUuid)) return;

			if (child.userData.isEdge || child.userData.isSelectedBuildingHighlight) {
				child.visible = !nextHidden;
			}
		});
		setBuildingsHidden(nextHidden);
	};

	const handleMinBounceChange = (value: number) => {
		const nextMin = Math.max(0, Math.floor(value));
		const nextMax = Math.max(nextMin, maxBounce);
		setMinBounce(nextMin);
		setMaxBounce(nextMax);
		setPhotonVisualizationBounceRange(nextMin, nextMax);
		if (showEnergyHeatmap && sceneRef.current) {
			refreshPhotonEnergyHeatmap(sceneRef.current);
			setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
		}
	};

	const handleMaxBounceChange = (value: number) => {
		const nextMax = Math.max(0, Math.floor(value));
		const clampedMax = Math.max(minBounce, nextMax);
		setMaxBounce(clampedMax);
		setPhotonVisualizationBounceRange(minBounce, clampedMax);
		if (showEnergyHeatmap && sceneRef.current) {
			refreshPhotonEnergyHeatmap(sceneRef.current);
			setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
		}
	};

	const handleToggleEnergyHeatmap = () => {
		if (!sceneRef.current) return;
		const next = !showEnergyHeatmap;
		setShowEnergyHeatmap(next);
		setPhotonEnergyHeatmapEnabled(sceneRef.current, next);
		setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
	};

	const handleSetHeatmapNormalizationMode = (mode: PhotonHeatmapNormalizationMode) => {
		if (!sceneRef.current) return;
		setHeatmapNormalizationMode(mode);
		setPhotonEnergyHeatmapNormalizationMode(sceneRef.current, mode);
		setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
	};

	const handleSetHeatmapCellSize = (value: number) => {
		if (!sceneRef.current) return;
		const nextCellSize = Math.max(1, Math.min(50, Math.round(value)));
		setHeatmapCellSize(nextCellSize);
		setPhotonEnergyHeatmapCellSize(sceneRef.current, nextCellSize);
		setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
	};

	const sunElevationDeg = () => {
		const pos = currentSunPositionRef.current;
		if (!pos) return null;
		const z = Math.max(-1, Math.min(1, pos.clone().normalize().z));
		return (Math.asin(z) * 180) / Math.PI;
	};

	const runTemporalSweep = async (options?: { skipScenePreparation?: boolean }) => {
		const { skipScenePreparation = false } = options ?? {};
		if (!sceneRef.current || !currentSunPositionRef.current) return;
		const startHour = Math.max(0, Math.min(23, Math.floor(sweepStartHour)));
		const endHour = Math.max(startHour, Math.min(23, Math.floor(sweepEndHour)));
		const step = Math.max(1, Math.floor(sweepStepHours));
		const hours: number[] = [];
		for (let hour = startHour; hour <= endHour; hour += step) {
			hours.push(hour);
		}
		if (hours.length === 0) return;

		setIsSweeping(true);
		setCalculationProgress(0);
		const originalTime = currentTime;
		try {
			if (!skipScenePreparation && onBeforeRunPhotonMapping) {
				await onBeforeRunPhotonMapping();
			}
			// Clear once at start so sweep accumulates cleanly
			// clearAllPhotons(sceneRef.current);
			setPhotonStats(getPhotonAccumulationStats());

			for (let index = 0; index < hours.length; index++) {
				const hour = hours[index];
				setCurrentTime(`${String(hour).padStart(2, '0')}:00`);
				// Wait for sun position to update
				await new Promise<void>((resolve) => setTimeout(resolve, 120));
				const elevation = sunElevationDeg();
				if (elevation === null || elevation <= 0) {
					console.log(`⏰ Skipping hour ${hour}:00 (Sun below horizon)`);
					continue;
				}
				await buildPhotonMapWithGridParallel(
					currentSunPositionRef.current,
					sceneRef.current,
					(progress) => {
						const normalized = ((index + progress) / hours.length) * 100;
						setCalculationProgress(Math.round(normalized));
					},
					{
						appendPaths: true,
						physicalContext: {
							date: currentDate,
							timeString: `${String(hour).padStart(2, '0')}:00`,
							latitude,
							longitude,
							atmosphericClarity: atmosphericClarity / 100,
							cloudCover: cloudCover / 100,
						},
					}
				);
			}
			setPhotonStats(getPhotonAccumulationStats());
			if (showEnergyHeatmap && sceneRef.current) {
				refreshPhotonEnergyHeatmap(sceneRef.current);
				setHeatmapLegend(getPhotonEnergyHeatmapLegendStats());
			}
		} catch (error) {
			console.error('Temporal sweep failed:', error);
		} finally {
			setCurrentTime(originalTime);
			setIsSweeping(false);
		}
	};

	const handleRunTemporalSweep = async () => {
		await runTemporalSweep();
	};

	const handleApplyAndRunTemporalSweep = async () => {
		await clearPhotonStateWithoutRebuild();
		await runTemporalSweep({ skipScenePreparation: true });
	};

	// Toggle all building materials between black and original color
	const [buildingsAreBlack, setBuildingsAreBlack] = useState(false);
	const handleSetBuildingsBlack = () => {
		if (!sceneRef.current) return;
		sceneRef.current.traverse((child) => {
			if (child instanceof Mesh && (child.userData.category === 'building' || child.userData.category === 'terrain')) {
				if (Array.isArray(child.material)) {
					child.material.forEach((mat) => {
						if (mat && 'color' in mat) {
							if (!buildingsAreBlack) {
								if (!mat.userData) mat.userData = {};
								if (!mat.userData._originalColor) {
									mat.userData._originalColor = mat.color.clone();
								}
								mat.color.set(0x000000);
							} else if (mat.userData && mat.userData._originalColor) {
								mat.color.copy(mat.userData._originalColor);
							}
						}
					});
				} else if (child.material && 'color' in child.material) {
					const mat = child.material;
					if (!buildingsAreBlack) {
						if (!mat.userData) mat.userData = {};
						if (!mat.userData._originalColor) {
							mat.userData._originalColor = mat.color.clone();
						}
						mat.color.set(0x000000);
					} else if (mat.userData && mat.userData._originalColor) {
						mat.color.copy(mat.userData._originalColor);
					}
				}
			}
		});
		setBuildingsAreBlack(!buildingsAreBlack);
	};

	if (isEnablingPhotonMapping) {
		return (
			<div className={photonControls}>
				<div className={photonControlsButtons}>
					<WeaveButton
						onClick={() => { }}
						disabled={true}
					>
						Enabling Photon Mapping…
					</WeaveButton>
				</div>
				<div className={photonStatsRow}>
					<div>Rebuilding scene with extruded walls for photon mapping...</div>
				</div>
			</div>
		);
	}

	if (!hasExtrudedWalls) {
		return (
			<div className={photonControls}>
				<div className={photonControlsButtons}>
					<WeaveButton
						onClick={handleEnablePhotonMapping}
						disabled={isBusy || !onBeforeRunPhotonMapping}
					>
						{isEnablingPhotonMapping ? 'Enabling Photon Mapping…' : 'Enable Photon Mapping'}
					</WeaveButton>
				</div>
				<div className={photonStatsRow}>
					<div>Enable photon mapping to rebuild the scene with extruded walls.</div>
				</div>
			</div>
		);
	}

	return (
		<div className={photonControls}>
			<div className={photonControlsButtons}>

				<WeaveButton onClick={handleClearPhotons} disabled={isBusy}>
					Disable Photon Mapping
				</WeaveButton>
			</div>
			<div className={photonStatsRow}>
				<div>Progress: {calculationProgress}%</div>
				<div>Capacity: {photonStats.capacityUsed.toFixed(1)}%</div>
				<div>Global: {photonStats.globalPhotons.toLocaleString()}</div>
				<div>Caustic: {photonStats.causticPhotons.toLocaleString()}</div>
				{sunElevationDeg() !== null && (
					<div>Sun: {sunElevationDeg()?.toFixed(1)}°</div>
				)}
			</div>
			<FormaTabs
				gap={8}
				density="medium"
			>
				<FormaTab for="photonMapping" label="Photon mapping" />
				<div id="photonMapping" slot="content">
					<div className={styles.tabContent}>
						<div className={styles.bounceFilterRow}>
							<label className={styles.bounceFilterLabel}>
								Clarity (%)
								<input
									type="number"
									min={0}
									max={100}
									step={1}
									value={atmosphericClarity}
									onChange={(event) => setAtmosphericClarity(Number(event.target.value))}
									disabled={isBusy}
									className={styles.bounceFilterInput}
								/>
							</label>
							<label className={styles.bounceFilterLabel}>
								Cloud cover (%)
								<input
									type="number"
									min={0}
									max={100}
									step={1}
									value={cloudCover}
									onChange={(event) => setCloudCover(Number(event.target.value))}
									disabled={isBusy}
									className={styles.bounceFilterInput}
								/>
							</label>
							<WeaveButton onClick={handleApplyAndRunPhotonMapping} disabled={isBusy}>
								Apply
							</WeaveButton>
						</div>
						<div className={primaryButtons}>
							<WeaveButton
								onClick={handleRunPhotonMapping}
								disabled={!photonStats.canAddMore || isBusy}
							>
								Run Photon Mapping
							</WeaveButton>
						</div>
					</div>
				</div>
				<FormaTab for="temporalSweep" label="Temporal sweep" />
				<div id="temporalSweep" slot="content">
					<div className={styles.tabContent}>
						<div className={styles.bounceFilterRow}>
							<label className={styles.bounceFilterLabel}>
								Clarity (%)
								<input
									type="number"
									min={0}
									max={100}
									step={1}
									value={atmosphericClarity}
									onChange={(event) => setAtmosphericClarity(clampNumber(event.target.value))}
									disabled={isBusy}
									className={styles.bounceFilterInput}
								/>
							</label>
							<label className={styles.bounceFilterLabel}>
								Cloud cover (%)
								<input
									type="number"
									min={0}
									max={100}
									step={1}
									value={cloudCover}
									onChange={(event) => setCloudCover(clampNumber(event.target.value))}
									disabled={isBusy}
									className={styles.bounceFilterInput}
								/>
							</label>
							<WeaveButton onClick={handleApplyAndRunTemporalSweep} disabled={isBusy}>
								Apply
							</WeaveButton>
						</div>
						<div className={styles.bounceFilterRow}>
							<label className={styles.bounceFilterLabel}>
								Start hour
								<input
									type="number"
									min={0}
									max={23}
									value={sweepStartHour}
									onChange={(e) => setSweepStartHour(Number(e.target.value))}
									disabled={isBusy}
									className={styles.bounceFilterInput}
								/>
							</label>
							<label className={styles.bounceFilterLabel}>
								End hour
								<input
									type="number"
									min={0}
									max={23}
									value={sweepEndHour}
									onChange={(e) => setSweepEndHour(Number(e.target.value))}
									disabled={isBusy}
									className={styles.bounceFilterInput}
								/>
							</label>
							<label className={styles.bounceFilterLabel}>
								Step (h)
								<input
									type="number"
									min={1}
									max={12}
									value={sweepStepHours}
									onChange={(e) => setSweepStepHours(Number(e.target.value))}
									disabled={isBusy}
									className={styles.bounceFilterInput}
								/>
							</label>
						</div>
						<WeaveButton onClick={handleRunTemporalSweep} disabled={isBusy}>
							{isSweeping ? `In progress...` : 'Run Temporal Sweep'}
						</WeaveButton>
					</div>
				</div>
				<FormaTab for="heatmapSettings" label='Heatmap & Settings' />
				<div id="heatmapSettings" slot="content">
					<div className={styles.tabContent}>
						<div className={styles.energyControls}>
							<WeaveCheckbox
								showLabel
								checked={photonsVisible}
								label={'Show photon visualizations'}
								change={() => handleTogglePhotonVisibility()}
								disabled={isBusy}
							/>
							<WeaveCheckbox
								showLabel
								checked={buildingsHidden}
								label={'Hide Buildings'}
								change={() => handleToggleBuildingVisibility()}
								disabled={isBusy}
							/>
							<WeaveCheckbox
								showLabel
								checked={buildingsAreBlack}
								label={'High contrast buildings'}
								change={() => handleSetBuildingsBlack()}
								disabled={isBusy}
							/>
						</div>
						<WeaveButton onClick={handleToggleEnergyHeatmap} disabled={isBusy}>
							{showEnergyHeatmap ? 'Hide Energy Heatmap' : 'Show Energy Heatmap'}
						</WeaveButton>
						<div className={styles.energyLegendContainer}>
							{showEnergyHeatmap && (
								<div className={styles.heatmapSettings}>
									<div className={styles.bounceFilterRow}>
										<label className={styles.bounceFilterLabel}>
											Min bounce
											<input
												type="number"
												min={0}
												max={PHOTON_MAP_CONFIG.maxBounces}
												value={minBounce}
												onChange={(event) =>
													handleMinBounceChange(Number(event.target.value))
												}
												disabled={isBusy}
												className={styles.bounceFilterInput}
											/>
										</label>
										<label className={styles.bounceFilterLabel}>
											Max bounce
											<input
												type="number"
												min={0}
												max={PHOTON_MAP_CONFIG.maxBounces}
												value={maxBounce}
												onChange={(event) =>
													handleMaxBounceChange(Number(event.target.value))
												}
												disabled={isBusy}
												className={styles.bounceFilterInput}
											/>
										</label>
										<label className={styles.bounceFilterLabel}>
											Cell size
											<input
												type="number"
												min={1}
												max={50}
												value={heatmapCellSize}
												onChange={(event) =>
													handleSetHeatmapCellSize(Number(event.target.value))
												}
												disabled={isBusy}
												className={styles.bounceFilterInput}
											/>
										</label>
									</div>
									<div className={styles.bounceFilterRow}>
										<span className={styles.bounceFilterLabel}>Heatmap mode</span>
										<WeaveButton
											onClick={() => handleSetHeatmapNormalizationMode('fixed')}
											disabled={isBusy || heatmapNormalizationMode === 'fixed'}
										>
											Fixed (0-800 W/m²)
										</WeaveButton>
										<WeaveButton
											onClick={() => handleSetHeatmapNormalizationMode('raw')}
											disabled={isBusy || heatmapNormalizationMode === 'raw'}
										>
											Raw
										</WeaveButton>
										<WeaveButton
											onClick={() => handleSetHeatmapNormalizationMode('percentile')}
											disabled={isBusy || heatmapNormalizationMode === 'percentile'}
										>
											Percentile
										</WeaveButton>
									</div>
								</div>
							)}
							{showEnergyHeatmap && heatmapNormalizationMode !== null && (() => {
								const min = heatmapLegend.displayMinEnergy;
								const max = heatmapLegend.displayMaxEnergy;
								const q1 = min + 0.25 * (max - min);
								const q2 = min + 0.5 * (max - min);
								const q3 = min + 0.75 * (max - min);
								return (
									<div className={styles.heatmapLegendContainer}>
										<div className={styles.bounceFilterLabel}>Irradiance (W/m2)</div>
										<div className={styles.heatmapLegendBarRow}>
											<div className={styles.heatmapLegendBar} />
										</div>
										<div className={styles.heatmapLegendTicks}>
											<span>{min.toFixed(2)}</span>
											<span>{q1.toFixed(2)}</span>
											<span>{q2.toFixed(2)}</span>
											<span>{q3.toFixed(2)}</span>
											<span>{max.toFixed(2)}</span>
										</div>
									</div>
								);
							})()}
						</div>
					</div>
				</div>
			</FormaTabs>
		</div>
	);
};
